import {
  PRODUCT_UI_STATE_ADD_NEW,
  PRODUCT_UI_STATE_SEARCH,
  PRODUCT_UI_STATE_SELECTED_EXISTING,
  PRODUCT_UI_STATE_UNKNOWN,
  PRODUCT_VISIBILITY_PUBLIC,
} from "../../../utlities/core.constants";
import { createEmptyProductPoc } from "../../../pages/Product/utils/productFormValidation";

/** True when the author typed product or POC fields worth carrying into Add New. */
function hasTypedProductContent(evaluation) {
  const product = evaluation?.product || {};
  const productFields = [
    product.name,
    product.version,
    product.description,
    product.category,
    product.pscCode,
    product.fedRampStatus,
  ];
  if (productFields.some((value) => value != null && String(value).trim() !== "")) return true;
  const poc = product.productPoc || {};
  return Object.values(poc).some((value) => value != null && String(value).trim() !== "");
}

/**
 * Reconcile an evaluation with the author's current access level for Public Mode (GSAACR-345).
 *
 * Public Mode is the anonymous tier of the same draft model as authenticated work (GSAACR-356): an
 * anonymous author has no server draft, so their report is flagged download-only
 * (`product_is_unknown`) — validation skips product identity and autosave/submit stay off while
 * download works. The moment they gain editor access (logging in mid-draft), that "no login"
 * restriction no longer applies, so the product they already typed graduates *in place* into the
 * normal Add-New-Product flow rather than being stranded as a download-only draft. Public Mode's
 * product form is a strict subset of the Add-New form, so nothing the author entered is lost.
 *
 * Returns the same reference when no change is needed so it is safe to run in a state updater on
 * every render without causing an update loop.
 *
 * @param {object|null} evaluation current evaluation state.
 * @param {boolean} isPublicMode whether the author currently lacks editor access.
 * @returns {object} the reconciled evaluation (possibly the same reference).
 */
export default function applyPublicModeLifecycle(evaluation, isPublicMode) {
  const prev = evaluation ?? {};

  if (isPublicMode) {
    // Public Mode always presents the generic, download-only product form. The authenticated product
    // state machine (search / add-new / selected) must NOT be active here, or its buttons, the
    // "finish your new product" reminder, and the add-new leave-guard would leak into the anonymous
    // view (e.g. a graduated draft carrying `add_new` back into Public Mode). A deliberate UNKNOWN
    // choice is kept so a brief access loss can restore it instead of graduating the product into
    // Add New. Ensure the download-only flag is set and any other ui-state is neutral.
    const needsFlag = !prev.product_is_unknown;
    const needsStateReset =
      prev.product_ui_state != null
      && prev.product_ui_state !== PRODUCT_UI_STATE_SEARCH
      && prev.product_ui_state !== PRODUCT_UI_STATE_UNKNOWN;
    if (!needsFlag && !needsStateReset) return prev;
    const next = { ...prev };
    if (needsFlag) next.product_is_unknown = true;
    if (needsStateReset) next.product_ui_state = PRODUCT_UI_STATE_SEARCH;
    return next;
  }

  // Gained access. A draft flagged download-only *by Public Mode* graduates back into the normal
  // authenticated flow; a buyer who deliberately chose an unknown product (UNKNOWN ui-state) is left
  // untouched, as is any draft that was never flagged download-only.
  if (!prev.product_is_unknown || prev.product_ui_state === PRODUCT_UI_STATE_UNKNOWN) {
    return prev;
  }

  // A real, server-backed product that was flipped into Public Mode mid-session (e.g. a token expiry)
  // keeps its product id through the download-only flagging. Restore it to the normal
  // selected-existing state so autosave/submit come back, instead of stranding it download-only
  // forever (the graduation used to skip any draft with a product id, which left this case stuck).
  if (prev.product?.id) {
    return {
      ...prev,
      product_is_unknown: false,
      product_ui_state: PRODUCT_UI_STATE_SELECTED_EXISTING,
    };
  }

  // Nothing typed (a browse, or "Log in to submit" with an empty product) returns to org-product
  // search. Graduating that empty draft into Add New would hide search and turn on save/submit.
  // product_is_unknown is still set here (the early return above already handled a clear flag).
  if (!hasTypedProductContent(prev)) {
    return {
      ...prev,
      product_is_unknown: false,
      product_ui_state: PRODUCT_UI_STATE_SEARCH,
    };
  }

  // An anonymous add-new draft (no product id) graduates *in place* into the Add-New-Product flow,
  // scaffolding the fields the authenticated product form expects. Nothing the author typed is lost.
  return {
    ...prev,
    product_is_unknown: false,
    product_ui_state: PRODUCT_UI_STATE_ADD_NEW,
    product: {
      ...prev.product,
      visibility: prev.product?.visibility || PRODUCT_VISIBILITY_PUBLIC,
      productPoc: prev.product?.productPoc ?? createEmptyProductPoc(),
    },
  };
}
